import { Badge } from '@heidi/design-system/badge';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { cn } from '@heidi/utils';
import { FormattedMessage } from 'react-intl';

import { planPeriodTabsMessages } from './plan-period-tabs-messages';
import type { PlanPeriodTabsProps } from './types';

function AnnualSaveBadge({ percent }: { percent: number }) {
  return (
    <Badge color="green" size="xs">
      <FormattedMessage {...planPeriodTabsMessages.savePercent} values={{ percent }} />
    </Badge>
  );
}

// The design system ships one 36px neutral segmented control, so the compact tier-toned
// toggle the pricing cards want is applied here as overrides rather than as new DS variants.
// `h-6` keeps the list's horizontal-orientation modifier so it outranks the DS `h-9` rule.
const smListClasses = 'p-0.5 group-data-[orientation=horizontal]/segmented-control:h-6';
const smTriggerClasses = 'h-5 gap-1 px-2 text-xs';
// Both states are toned the same; the white indicator carries the selection on its own.
const proTierTriggerClasses =
  'text-tier-pro-text hover:text-tier-pro-text data-[state=active]:text-tier-pro-text';

/** One locked Yearly item, sized like `PlanPeriodTabs` at `sm`, for plans sold yearly only. */
export function YearlyOnlyPeriodTab({ label }: { label: string }) {
  return (
    <SegmentedControl value="annual" className="shrink-0">
      <SegmentedControlList className={smListClasses}>
        <SegmentedControlTrigger value="annual" className={smTriggerClasses}>
          {label}
        </SegmentedControlTrigger>
      </SegmentedControlList>
    </SegmentedControl>
  );
}

/** Dumb annual/monthly billing period toggle. */
export function PlanPeriodTabs({
  value,
  onValueChange,
  annualSavePercent,
  saveBadgePlacement = 'inTab',
  size = 'md',
  variant = 'default',
  className,
}: PlanPeriodTabsProps) {
  const savePercent =
    annualSavePercent != null && annualSavePercent > 0 ? annualSavePercent : null;
  const showBeside =
    saveBadgePlacement === 'start' && savePercent != null && value === 'annual';
  const showInTab = saveBadgePlacement === 'inTab' && savePercent != null;
  const triggerClassName = cn(
    size === 'sm' && smTriggerClasses,
    variant === 'proTier' && proTierTriggerClasses
  );

  return (
    <div
      className={cn(
        'flex items-center gap-2',
        saveBadgePlacement === 'start' && 'w-full',
        className
      )}
    >
      {showBeside && savePercent != null ? (
        <AnnualSaveBadge percent={savePercent} />
      ) : null}
      <SegmentedControl
        className={saveBadgePlacement === 'start' ? 'ms-auto' : undefined}
        value={value}
        onValueChange={(next) => {
          if (next === 'annual' || next === 'monthly') {
            onValueChange(next);
          }
        }}
      >
        <SegmentedControlList
          className={cn(
            size === 'sm' && smListClasses,
            variant === 'proTier' && 'bg-tier-pro-muted'
          )}
        >
          <SegmentedControlTrigger value="annual" className={triggerClassName}>
            <FormattedMessage {...planPeriodTabsMessages.annual} />
            {showInTab && savePercent != null ? (
              <AnnualSaveBadge percent={savePercent} />
            ) : null}
          </SegmentedControlTrigger>
          <SegmentedControlTrigger value="monthly" className={triggerClassName}>
            <FormattedMessage {...planPeriodTabsMessages.monthly} />
          </SegmentedControlTrigger>
        </SegmentedControlList>
      </SegmentedControl>
    </div>
  );
}
